/**
 * ContentConfigPage
 * Main page for configuring content generation settings (Story 19)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Settings,
  Save,
  RefreshCw,
  AlertCircle,
  Check,
  Globe,
  Ruler,
  Cpu,
  Zap,
  DollarSign,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/context/auth-context';
import {
  useContentConfig,
  DEFAULT_CONFIG,
  type ContentGenerationConfig,
  type ToneConfig,
  type LengthConfig,
  type PricingConfig as PricingConfigType,
  type BrandConfig as BrandConfigType,
  type LanguageConfig,
  type ProductDetailsConfig as ProductDetailsConfigType,
  type ContentType,
  type ResolvedConfig,
} from '@/hooks/useContentConfig';
import {
  ToneSelector,
  LengthSliders,
  PricingConfig,
  BrandConfig,
  ProductDetailsConfig,
  ConfigPreview,
  RegenerationModal,
} from '@/components/config';
import { ProcessingSettings } from '@/components/site-settings/processing-settings';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'nl', label: 'Dutch' },
  { value: 'pl', label: 'Polish' },
  { value: 'ca', label: 'Catalan' },
];

const UNITS_OPTIONS = [
  { value: 'metric', label: 'Metric (km, kg, \u00b0C)' },
  { value: 'imperial', label: 'Imperial (mi, lb, \u00b0F)' },
  { value: 'both', label: 'Both' },
];

// LLM model options for AEO generation with cost/quality indicators
// Grouped by provider: Google (Vertex AI), OpenAI, Anthropic, OpenRouter-only
// Note: Only OpenAI and Vertex AI have Batch APIs. Other providers use real-time API
// for single generations but fall back to OpenAI Batch API for bulk feed imports.
const AEO_MODEL_OPTIONS = [
  // === GOOGLE (Direct Vertex AI) - Native batch support ===
  {
    value: 'gemini-2.0-flash',
    label: 'Gemini 2.0 Flash',
    description: 'Best value - Good quality at lowest cost',
    cost: '$100/1M products',
    quality: 'Good',
    speed: 'Fast',
    provider: 'Google',
    recommended: true,
    batchModel: 'gemini-2.0-flash',
    batchProvider: 'Vertex AI',
    hasBatchApi: true,
  },
  {
    value: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash',
    description: 'Premium quality - Best for luxury brands',
    cost: '$150/1M products',
    quality: 'Excellent',
    speed: 'Fast',
    provider: 'Google',
    recommended: false,
    batchModel: 'gemini-2.5-flash',
    batchProvider: 'Vertex AI',
    hasBatchApi: true,
  },
  {
    value: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro',
    description: 'Top quality - Highest reasoning depth',
    cost: '$300/1M products',
    quality: 'Best',
    speed: 'Medium',
    provider: 'Google',
    recommended: false,
    batchModel: 'gemini-2.5-pro',
    batchProvider: 'Vertex AI',
    hasBatchApi: true,
  },
  // === OPENAI (Direct) - Native batch support ===
  {
    value: 'gpt-4o-mini',
    label: 'GPT-4o Mini',
    description: 'Fast turnaround - OpenAI balanced',
    cost: '$150/1M products',
    quality: 'Good',
    speed: 'Fast',
    provider: 'OpenAI',
    recommended: false,
    batchModel: 'gpt-4o-mini',
    batchProvider: 'OpenAI',
    hasBatchApi: true,
  },
  {
    value: 'gpt-4o',
    label: 'GPT-4o',
    description: 'High quality - Premium pricing',
    cost: '$2,500/1M products',
    quality: 'Excellent',
    speed: 'Medium',
    provider: 'OpenAI',
    recommended: false,
    batchModel: 'gpt-4o',
    batchProvider: 'OpenAI',
    hasBatchApi: true,
  },
  // === ANTHROPIC (Direct) - NO batch API, falls back to OpenAI for bulk ===
  {
    value: 'claude-3.5-haiku',
    label: 'Claude 3.5 Haiku',
    description: 'Fast & affordable - Great for high volume',
    cost: '$250/1M products',
    quality: 'Good',
    speed: 'Very Fast',
    provider: 'Anthropic',
    recommended: false,
    batchModel: 'gpt-4o-mini',
    batchProvider: 'OpenAI',
    hasBatchApi: false,
  },
  {
    value: 'claude-3.5-sonnet',
    label: 'Claude 3.5 Sonnet',
    description: 'Premium quality - Best reasoning',
    cost: '$3,000/1M products',
    quality: 'Excellent',
    speed: 'Medium',
    provider: 'Anthropic',
    recommended: false,
    batchModel: 'gpt-4o',
    batchProvider: 'OpenAI',
    hasBatchApi: false,
  },
  // === OPENROUTER (Exclusive models) - NO batch API, falls back to OpenAI for bulk ===
  {
    value: 'deepseek-chat',
    label: 'DeepSeek Chat',
    description: 'Cost-effective - Good for bulk generation',
    cost: '$140/1M products',
    quality: 'Good',
    speed: 'Fast',
    provider: 'DeepSeek',
    recommended: false,
    batchModel: 'gpt-4o-mini',
    batchProvider: 'OpenAI',
    hasBatchApi: false,
  },
  {
    value: 'deepseek-reasoner',
    label: 'DeepSeek Reasoner',
    description: 'Advanced reasoning - Complex products',
    cost: '$550/1M products',
    quality: 'Excellent',
    speed: 'Slow',
    provider: 'DeepSeek',
    recommended: false,
    batchModel: 'gpt-4o',
    batchProvider: 'OpenAI',
    hasBatchApi: false,
  },
  {
    value: 'qwen-2.5-72b',
    label: 'Qwen 2.5 72B',
    description: 'Strong multilingual - Great for intl sites',
    cost: '$350/1M products',
    quality: 'Very Good',
    speed: 'Medium',
    provider: 'Alibaba',
    recommended: false,
    batchModel: 'gpt-4o',
    batchProvider: 'OpenAI',
    hasBatchApi: false,
  },
];

export default function ContentConfigPage() {
  const { selectedSite } = useSite();
  const { toast } = useToast();
  const { user } = useAuth();
  const siteId = selectedSite?.id || selectedSite?._id;
  const isSuperAdmin = user?.role === 'super_admin';

  const {
    configurations,
    loading,
    error,
    saving,
    saveConfiguration,
    resolveConfig,
    generatePreview,
    estimateRegeneration,
    startRegeneration,
    getJobStatus,
    cancelJob,
  } = useContentConfig({ siteId });

  // Local state for editing
  const [activeTab, setActiveTab] = useState('tone');
  const [hasChanges, setHasChanges] = useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [resolvedConfig, setResolvedConfig] = useState<ResolvedConfig | null>(null);

  // Config state (start with defaults)
  const [toneConfig, setToneConfig] = useState<ToneConfig>(DEFAULT_CONFIG.tone);
  const [lengthConfig, setLengthConfig] = useState<LengthConfig>(DEFAULT_CONFIG.length);
  const [pricingConfig, setPricingConfig] = useState<PricingConfigType>(DEFAULT_CONFIG.pricing);
  const [brandConfig, setBrandConfig] = useState<BrandConfigType>(DEFAULT_CONFIG.brand);
  const [languageConfig, setLanguageConfig] = useState<LanguageConfig>(DEFAULT_CONFIG.language);
  const [productDetailsConfig, setProductDetailsConfig] = useState<ProductDetailsConfigType>(DEFAULT_CONFIG.productDetails || {});
  const [aeoLlmModel, setAeoLlmModel] = useState<string>('gemini-2.0-flash');

  // Load site config into local state when configurations change
  useEffect(() => {
    if (configurations?.site) {
      const site = configurations.site;
      setToneConfig(site.tone || DEFAULT_CONFIG.tone);
      setLengthConfig(site.length || DEFAULT_CONFIG.length);
      setPricingConfig(site.pricing || DEFAULT_CONFIG.pricing);
      setBrandConfig(site.brand || DEFAULT_CONFIG.brand);
      setLanguageConfig(site.language || DEFAULT_CONFIG.language);
      setProductDetailsConfig(site.productDetails || DEFAULT_CONFIG.productDetails || {});
      setAeoLlmModel(site.aeoLlmModel || 'gemini-2.0-flash');
      setHasChanges(false);
    }
  }, [configurations]);

  // Resolve config on load
  useEffect(() => {
    if (siteId) {
      resolveConfig().then(setResolvedConfig);
    }
  }, [siteId, resolveConfig]);

  // Handle config changes
  const handleToneChange = useCallback((tone: ToneConfig) => {
    setToneConfig(tone);
    setHasChanges(true);
  }, []);

  const handleLengthChange = useCallback((length: LengthConfig) => {
    setLengthConfig(length);
    setHasChanges(true);
  }, []);

  const handlePricingChange = useCallback((pricing: PricingConfigType) => {
    setPricingConfig(pricing);
    setHasChanges(true);
  }, []);

  const handleBrandChange = useCallback((brand: BrandConfigType) => {
    setBrandConfig(brand);
    setHasChanges(true);
  }, []);

  const handleLanguageChange = useCallback((language: LanguageConfig) => {
    setLanguageConfig(language);
    setHasChanges(true);
  }, []);

  const handleProductDetailsChange = useCallback((productDetails: ProductDetailsConfigType) => {
    setProductDetailsConfig(productDetails);
    setHasChanges(true);
  }, []);

  const handleModelChange = useCallback((model: string) => {
    setAeoLlmModel(model);
    setHasChanges(true);
  }, []);

  // Save configuration
  const handleSave = async () => {
    try {
      await saveConfiguration({
        scope: 'site',
        tone: toneConfig,
        length: lengthConfig,
        pricing: pricingConfig,
        brand: brandConfig,
        language: languageConfig,
        productDetails: productDetailsConfig,
        aeoLlmModel: aeoLlmModel,
        isActive: true,
      });

      // Refresh resolved config
      const resolved = await resolveConfig();
      setResolvedConfig(resolved);

      setHasChanges(false);
      toast({
        title: 'Configuration Saved',
        description: 'Your content generation settings have been updated.',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to save configuration. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Generate preview handler - pass current form state for unsaved changes
  const handleGeneratePreview = async (contentType: ContentType) => {
    try {
      return await generatePreview('', contentType, {
        tone: toneConfig,
        length: lengthConfig,
        pricing: pricingConfig,
        brand: brandConfig,
        language: languageConfig,
      });
    } catch (err) {
      toast({
        title: 'Preview Error',
        description: 'Failed to generate preview. Please try again.',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Regeneration handlers
  const handleEstimate = async (contentTypes: ContentType[]) => {
    return await estimateRegeneration(undefined, contentTypes);
  };

  const handleStartRegeneration = async (contentTypes: ContentType[]) => {
    return await startRegeneration(undefined, contentTypes);
  };

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to configure content generation settings.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading && !configurations) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Content Generation Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure how AI-generated content sounds, looks, and behaves for {selectedSite?.name || 'your site'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Badge variant="secondary" className="animate-pulse">
              Unsaved changes
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={() => setShowRegenModal(true)}
            disabled={saving}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Regenerate Content
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* AI Model Selection Card */}
      <Card className="border-2 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cpu className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">AI Model for AEO Generation</CardTitle>
            </div>
            {resolvedConfig?.sources?.aeoLlmModel && (
              <Badge variant="outline" className="text-xs">
                From: {resolvedConfig.sources.aeoLlmModel}
              </Badge>
            )}
          </div>
          <CardDescription>
            Select which AI model to use for generating product and category content. Single product generations use your selected model directly.
            For bulk feed imports, models without a Batch API (Claude, DeepSeek, Qwen) use an equivalent OpenAI model for cost efficiency.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {AEO_MODEL_OPTIONS.map((option) => (
              <div
                key={option.value}
                onClick={() => handleModelChange(option.value)}
                className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  aeoLlmModel === option.value
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50 hover:bg-muted/50'
                }`}
              >
                {option.recommended && (
                  <Badge className="absolute -top-2 -right-2 text-xs" variant="default">
                    Recommended
                  </Badge>
                )}
                <div className="font-medium text-sm">{option.label}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {option.description}
                </div>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2 mt-3 text-xs">
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {option.cost}
                    </span>
                  </div>
                )}
                <div className={`flex items-center gap-2 ${isSuperAdmin ? 'mt-1' : 'mt-3'} text-xs text-muted-foreground`}>
                  <span className="flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    {option.speed}
                  </span>
                  <span>•</span>
                  <span>{option.quality} quality</span>
                </div>
                {/* Batch model info - show when model doesn't have native batch API */}
                {!option.hasBatchApi && (
                  <div className="mt-2 pt-2 border-t border-border/50 text-xs text-amber-600 dark:text-amber-400">
                    <span className="font-medium">Feed imports:</span> Uses {option.batchModel} via {option.batchProvider} Batch API
                  </div>
                )}
                {aeoLlmModel === option.value && (
                  <Check className="absolute top-2 right-2 h-4 w-4 text-primary" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Configuration Tabs */}
        <div className="xl:col-span-2">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="tone">Tone</TabsTrigger>
              <TabsTrigger value="length">Length</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="brand">Brand</TabsTrigger>
              <TabsTrigger value="language">Language</TabsTrigger>
              <TabsTrigger value="product">Product</TabsTrigger>
            </TabsList>

            <div className="mt-4">
              <TabsContent value="tone" className="mt-0">
                <ToneSelector
                  value={toneConfig}
                  onChange={handleToneChange}
                  sourceLabel={resolvedConfig?.sources?.tone}
                />
              </TabsContent>

              <TabsContent value="length" className="mt-0">
                <LengthSliders
                  value={lengthConfig}
                  onChange={handleLengthChange}
                  sourceLabel={resolvedConfig?.sources?.length}
                />
              </TabsContent>

              <TabsContent value="pricing" className="mt-0">
                <PricingConfig
                  value={pricingConfig}
                  onChange={handlePricingChange}
                  sourceLabel={resolvedConfig?.sources?.pricing}
                />
              </TabsContent>

              <TabsContent value="brand" className="mt-0">
                <BrandConfig
                  value={brandConfig}
                  onChange={handleBrandChange}
                  sourceLabel={resolvedConfig?.sources?.brand}
                  brandName={selectedSite?.name}
                />
              </TabsContent>

              <TabsContent value="language" className="mt-0">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-cyan-500" />
                        <CardTitle className="text-lg">Language & Formatting</CardTitle>
                      </div>
                      {resolvedConfig?.sources?.language && (
                        <Badge variant="outline" className="text-xs">
                          From: {resolvedConfig.sources.language}
                        </Badge>
                      )}
                    </div>
                    <CardDescription>
                      Configure language, regional conventions, and measurement units
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="language">Primary Language</Label>
                        <Select
                          value={languageConfig.primaryLanguage}
                          onValueChange={(v) => handleLanguageChange({ ...languageConfig, primaryLanguage: v })}
                        >
                          <SelectTrigger id="language">
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                          <SelectContent>
                            {LANGUAGE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="units">Measurement Units</Label>
                        <Select
                          value={languageConfig.units}
                          onValueChange={(v) => handleLanguageChange({ ...languageConfig, units: v as 'metric' | 'imperial' | 'both' })}
                        >
                          <SelectTrigger id="units">
                            <SelectValue placeholder="Select units" />
                          </SelectTrigger>
                          <SelectContent>
                            {UNITS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="product" className="mt-0">
                <ProductDetailsConfig
                  value={productDetailsConfig}
                  onChange={handleProductDetailsChange}
                  sourceLabel={resolvedConfig?.sources?.productDetails}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Preview Panel */}
        <div className="xl:col-span-1">
          <ConfigPreview
            resolvedConfig={resolvedConfig}
            onGeneratePreview={handleGeneratePreview}
            loading={loading}
          />
        </div>
      </div>

      {/* Regeneration Modal */}
      <RegenerationModal
        isOpen={showRegenModal}
        onClose={() => setShowRegenModal(false)}
        onEstimate={handleEstimate}
        onStart={handleStartRegeneration}
        onGetStatus={getJobStatus}
        onCancel={cancelJob}
      />

      {/* Processing Settings (Super Admin Only) */}
      {isSuperAdmin && siteId && (
        <ProcessingSettings siteId={siteId} />
      )}
    </div>
  );
}
